import { useMemo, useState } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import {
  BarChart3,
  CheckCircle,
  Images,
  LogOut,
  Menu,
  Radar,
  RefreshCw,
  Store,
  X,
} from 'lucide-react';
import { signOut } from '@/lib/authBootstrap';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuthUser } from '@/hooks/useAuthUser';
import { usePortalBranding } from '@/hooks/usePortalBranding';
import type { PortalContextResponse } from '@shared/portal-api-types';
import { buildPortalSectionHref, portalNavPathname } from './portalCycleQuery';

interface PortalShellProps {
  context: PortalContextResponse;
  children: React.ReactNode;
}

interface PortalNavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

function isNavActive(href: string, location: string): boolean {
  const pathname = portalNavPathname(href);
  if (pathname === '/portal') {
    return location === '/portal' || location.startsWith('/portal/campaigns');
  }
  return location === pathname || location.startsWith(`${pathname}/`);
}

export function PortalShell({ context, children }: PortalShellProps) {
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user } = useAuthUser();
  const { branding } = usePortalBranding();

  const retailerLabel = context.allowedRetailers.map((r) => r.name).join(', ');

  const navItems = useMemo(() => {
    const items: PortalNavItem[] = [];
    if (context.features.campaigns) {
      items.push({
        name: 'Campaigns',
        href: buildPortalSectionHref('/portal', search),
        icon: BarChart3,
      });
    }
    if (context.features.cycles) {
      items.push({
        name: 'Cycles',
        href: buildPortalSectionHref('/portal/cycles', search),
        icon: RefreshCw,
      });
    }
    if (context.features.cockpit) {
      items.push({
        name: 'Cockpit',
        href: buildPortalSectionHref('/portal/cockpit', search),
        icon: Radar,
      });
    }
    if (context.features.compliance) {
      items.push({
        name: 'Compliance',
        href: buildPortalSectionHref('/portal/compliance', search),
        icon: CheckCircle,
      });
    }
    if (context.features.gallery) {
      items.push({
        name: 'Campaign gallery',
        href: buildPortalSectionHref('/portal/gallery', search),
        icon: Images,
      });
    }
    if (context.features.stores) {
      items.push({
        name: 'Stores',
        href: buildPortalSectionHref('/portal/stores', search),
        icon: Store,
      });
    }
    return items;
  }, [context.features, search]);

  const userInitial = (user?.name || user?.username || user?.email || 'P').charAt(0).toUpperCase();
  const userLabel = user?.name || user?.username || user?.email || 'Partner user';

  return (
    <div className="min-h-screen bg-background theme-mosaic">
      <div className="lg:hidden fixed top-4 left-4 z-[70]">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSidebarOpen((open) => !open)}
          className="bg-card shadow-md touch-manipulation min-h-[44px] min-w-[44px]"
          aria-label="Toggle navigation"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      {sidebarOpen ? (
        <div
          className="fixed inset-0 bg-black/50 z-[45] lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      ) : null}

      <nav
        className={cn(
          'fixed left-0 top-0 h-full w-64 shadow-lg transform transition-transform duration-300 ease-in-out z-[50]',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        style={{
          backgroundColor: 'var(--navigation-bg, #0F3A3F)',
          color: 'var(--navigation-text, #FFFFFF)',
        }}
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt="Logo"
                  className="h-8 max-w-[180px] object-contain"
                />
              ) : (
                <h1 className="text-xl font-semibold">ReActor</h1>
              )}
              <p className="mt-2 text-xs uppercase tracking-wide opacity-70">Partner portal</p>
              <p className="mt-1 truncate text-sm font-medium opacity-90">
                {retailerLabel || context.organisation.key}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden shrink-0 text-inherit hover:bg-black/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <div className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isNavActive(item.href, location);
              return (
                <Link key={item.href} href={item.href}>
                  <div
                    className={cn(
                      'flex items-center py-3 px-4 text-sm font-medium rounded-lg hover:bg-black/5 transition-colors cursor-pointer',
                      isActive ? 'font-semibold border-r-2 border-white/50' : '',
                    )}
                    style={isActive ? { backgroundColor: 'var(--nav-active, rgba(255,255,255,0.2))' } : undefined}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="h-5 w-5 mr-3 opacity-70" />
                    <span>{item.name}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 p-4 border-t space-y-3"
          style={{
            backgroundColor: 'var(--navigation-bg, #0F3A3F)',
            borderTopColor: 'rgba(255, 255, 255, 0.1)',
          }}
        >
          <div className="flex items-center min-w-0">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-medium">
              {userInitial}
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium truncate">{userLabel}</p>
              <p className="text-xs opacity-70 truncate">{context.organisation.key}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-inherit hover:bg-black/10"
            onClick={async () => {
              await signOut();
              setLocation('/login');
            }}
          >
            <LogOut className="h-3 w-3 mr-2" />
            Sign out
          </Button>
        </div>
      </nav>

      <div className="lg:ml-64 min-h-screen bg-background">
        <main className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
